import { useMemo, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Plus, ChevronRight, Play } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { PageHeader } from '@/components/common/PageHeader'
import { SummaryStrip } from '@/components/common/SummaryStrip'
import type { SummaryPill } from '@/components/common/SummaryStrip'
import { EmptyState } from '@/components/common/EmptyState'
import { RunListSkeleton } from '@/components/skeletons/RunListSkeleton'
import { RunStatusBadge } from '@/components/runs/RunStatusBadge'
import { RunFilters } from '@/components/runs/RunFilters'
import type { RunsFilterParams } from '@/components/runs/RunFilters'
import { RelativeTime } from '@/components/data-display/RelativeTime'
import { DataTablePagination } from '@/components/data-display/DataTablePagination'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useRuns } from '@/api/runs'
import type { Run, RunStatus, RunsFilterParams as ApiRunsFilterParams } from '@/lib/types'

const PAGE_SIZES = [10, 25, 50, 100]

/**
 * Runs list page with filtering and pagination.
 * Route: /#/runs
 */
export function RunListPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Parse URL params with defaults
  const page = parseInt(searchParams.get('page') ?? '1', 10)
  const pageSize = parseInt(searchParams.get('pageSize') ?? '10', 10)
  
  // Parse filter params from URL
  const filters = useMemo((): RunsFilterParams => {
    const statusParam = searchParams.get('status')
    const searchParam = searchParams.get('search')
    const projectIdParam = searchParams.get('projectId')
    const dateFromParam = searchParams.get('dateFrom')
    const dateToParam = searchParams.get('dateTo')
    
    return {
      status: statusParam ? statusParam.split(',') as RunStatus[] : undefined,
      search: searchParam || undefined,
      projectId: projectIdParam || undefined,
      dateFrom: dateFromParam ? new Date(dateFromParam) : undefined,
      dateTo: dateToParam ? new Date(dateToParam) : undefined,
      limit: pageSize,
      offset: (page - 1) * pageSize
    }
  }, [searchParams, page, pageSize])

  // Build query params for API
  const queryParams = useMemo((): ApiRunsFilterParams => {
    const params: ApiRunsFilterParams = {
      offset: filters.offset,
      limit: filters.limit,
    }
    
    if (filters.status && filters.status.length > 0) {
      params.status = filters.status
    }
    
    if (filters.dateFrom) {
      params.start_date = filters.dateFrom.toISOString()
    }
    if (filters.dateTo) {
      params.end_date = filters.dateTo.toISOString()
    }
    
    if (filters.projectId) {
      params.projectId = filters.projectId
    }
    
    return params
  }, [filters])

  const { data: runsResponse, isLoading, error } = useRuns(queryParams)

  // Handle filter changes
  const handleFiltersChange = useCallback((newFilters: RunsFilterParams) => {
    const newParams = new URLSearchParams()
    
    if (newFilters.search) {
      newParams.set('search', newFilters.search)
    }
    if (newFilters.status && newFilters.status.length > 0) {
      newParams.set('status', newFilters.status.join(','))
    }
    if (newFilters.projectId) {
      newParams.set('projectId', newFilters.projectId)
    }
    if (newFilters.dateFrom) {
      newParams.set('dateFrom', newFilters.dateFrom.toISOString().split('T')[0])
    }
    if (newFilters.dateTo) {
      newParams.set('dateTo', newFilters.dateTo.toISOString().split('T')[0])
    }
    
    // Keep pagination params if not reset
    if (newFilters.offset !== 0) {
      const newPage = Math.floor((newFilters.offset ?? 0) / pageSize) + 1
      newParams.set('page', newPage.toString())
    }
    if (pageSize !== 10) {
      newParams.set('pageSize', pageSize.toString())
    }
    
    setSearchParams(newParams)
  }, [pageSize, setSearchParams])

  const handlePageChange = useCallback((newPage: number) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('page', newPage.toString())
    setSearchParams(newParams)
  }, [searchParams, setSearchParams])

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('pageSize', newPageSize.toString())
    newParams.set('page', '1')
    setSearchParams(newParams)
  }, [searchParams, setSearchParams])

  const runs = runsResponse?.runs ?? []
  const totalCount = runsResponse?.total ?? 0

  // Derive summary pills from loaded runs (must be before early returns to respect Rules of Hooks)
  const summaryPills = useMemo((): SummaryPill[] => {
    const running = runs.filter(r => r.status === 'RUNNING').length
    const queued = runs.filter(r => r.status === 'QUEUED').length
    const failed = runs.filter(r => r.status === 'FAILED').length
    return [
      { label: 'Total', value: totalCount, variant: 'default' as const },
      { label: 'Running', value: running, variant: 'primary' as const },
      { label: 'Queued', value: queued, variant: 'warning' as const },
      { label: 'Failed', value: failed, variant: 'error' as const },
    ]
  }, [runs, totalCount])

  if (isLoading) {
    return (
      <PageShell>
        <div className="p-6 space-y-6">
          <PageHeader
            title="Runs"
            actions={
              <Button asChild>
                <Link to="/runs/new">
                  <Plus className="h-4 w-4 mr-2" />
                  New Run
                </Link>
              </Button>
            }
          />
          <RunListSkeleton />
        </div>
      </PageShell>
    )
  }

  if (error) {
    return (
      <PageShell>
        <div className="p-6 space-y-6">
          <div className="text-center text-red-500">
            Failed to load runs. Please try again.
          </div>
        </div>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <div className="p-6 space-y-6">
        <PageHeader
          title="Runs"
          actions={
            <Button asChild>
              <Link to="/runs/new">
                <Plus className="h-4 w-4 mr-2" />
                New Run
              </Link>
            </Button>
          }
        />

        {/* Summary Strip */}
        <SummaryStrip pills={summaryPills} isLoading={isLoading} />

        {/* Filters — unwrapped for cleaner look */}
        <RunFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
        />

        {/* Runs Table */}
        {runs.length === 0 && !isLoading ? (
          <EmptyState
            icon={Play}
            title="No runs found"
            description="No runs match your filters. Create a new run or adjust the criteria."
            action={
              <Button asChild>
                <Link to="/runs/new">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Run
                </Link>
              </Button>
            }
          />
        ) : runs.length > 0 ? (
          <Card>
            <CardContent className="pt-4">
              <TooltipProvider>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Status</TableHead>
                      <TableHead>Run ID</TableHead>
                      <TableHead>Repository</TableHead>
                      <TableHead className="max-w-[250px]">Goal</TableHead>
                      <TableHead className="text-right">Created</TableHead>
                      <TableHead className="w-[40px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {runs.map((run: Run) => (
                      <TableRow
                        key={run.run_id}
                        className="group cursor-pointer hover:bg-accent/50 transition-colors"
                        onClick={() => window.location.hash = `#/runs/${run.run_id}`}
                      >
                        <TableCell>
                          <RunStatusBadge status={run.status} size="sm" />
                        </TableCell>
                        <TableCell>
                          <Link
                            to={`/runs/${run.run_id}`}
                            className="font-mono text-sm hover:underline text-primary"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {truncateRunId(run.run_id)}
                          </Link>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {extractRepoName(run.repo_url)}
                        </TableCell>
                        <TableCell className="max-w-[250px]">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-sm truncate block">{run.goal}</span>
                            </TooltipTrigger>
                            {run.goal && run.goal.length > 40 && (
                              <TooltipContent className="max-w-sm">
                                <p className="whitespace-pre-wrap">{run.goal}</p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TableCell>
                        <TableCell className="text-right">
                          <RelativeTime date={run.timestamps?.created} className="text-xs text-muted-foreground" />
                        </TableCell>
                        <TableCell>
                          <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TooltipProvider>

              {/* Pagination */}
              <div className="flex items-center justify-end mt-2">
                <DataTablePagination
                  page={page}
                  pageSize={pageSize}
                  total={totalCount}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  pageSizeOptions={PAGE_SIZES}
                  itemName="runs"
                />
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </PageShell>
  )
}

/**
 * Truncate run ID for display
 */
function truncateRunId(runId: string | undefined | null): string {
  if (!runId) return '—'
  if (runId.length <= 20) return runId
  return `${runId.slice(0, 12)}...${runId.slice(-4)}`
}

/**
 * Extract repo name from URL
 */
function extractRepoName(url: string | undefined | null): string {
  if (!url) return '—'
  try {
    // Handle Azure DevOps URLs: https://dev.azure.com/org/project/_git/repo
    const match = url.match(/_git\/([^/?]+)/)
    if (match) return match[1]

    // Handle GitHub URLs: https://github.com/org/repo
    const parts = url.split('/')
    return parts[parts.length - 1] || url
  } catch {
    return url
  }
}

export default RunListPage

